import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Checkbox, FormControlLabel, CircularProgress, Link } from '@mui/material';
import { styled } from '@mui/system';
import { Trans, useTranslation } from 'react-i18next';
import { getLegalUrlWithReturn } from '../../utils/legalUrls';

import TextInput from '../Inputs/TextInput';
import Subtitle from '../Texts/Subtitle';
import OutlineButton from '../Buttons/OutlineButton';
import MainLogo from '../Titles/MainLogo';
import ConnectFormContainer from '../Containers/ConnectFormContainer';
import SmallLink from '../Links/SmallLink';
import SmallText from '../Texts/SmallText';
import GoogleButton from '../Buttons/GoogleButton';
import WhiteDivider from '../Components/WhiteDivider';
import MusicStyleSelector from './MusicStyleSelector';
import { getApiUrl } from '../../config/api';
import { buildGoogleOAuthUrl } from '../../utils/googleOAuthUrl';
import { mapApiErrorsToFields } from '../../utils/mapApiErrors';
import { translateApiError } from '../../utils/translateApiError';

const StyledCheckbox = styled(Checkbox)(({ theme }: any) => ({
  color: theme.palette.primary.main,
  '&.Mui-checked': { color: theme.palette.primary.main },
}));

interface FormData {
  email: string;
  password: string;
  nickname: string;
  firstName: string;
  lastName: string;
  phone: string;
  city: string;
  postalCode: string;
  termsConsent: boolean;
}

interface FormErrors {
  [key: string]: string | null;
}

type RegisterStep = 'account' | 'styles';

const ACCOUNT_ERROR_FIELDS = [
  'email',
  'password',
  'nickname',
  'firstName',
  'lastName',
  'phone',
  'city',
  'postalCode',
  'termsConsent',
];

const RegisterForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<RegisterStep>('account');
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    nickname: '',
    firstName: '',
    lastName: '',
    phone: '',
    city: '',
    postalCode: '',
    termsConsent: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [success, setSuccess] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingNickname, setCheckingNickname] = useState(false);

  useEffect(() => {
    const nickname = formData.nickname.trim();
    if (nickname.length < 2) {
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setCheckingNickname(true);
      try {
        const res = await fetch(
          getApiUrl(`/api/v1/auth/nickname-available?nickname=${encodeURIComponent(nickname)}`),
          { credentials: 'include' },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.available === false) {
          setErrors((prev) => ({
            ...prev,
            nickname: data.reason === 'taken' ? t('auth.nicknameTaken') : t('auth.nicknameInvalid'),
          }));
        } else {
          setErrors((prev) => {
            const takenMsg = t('auth.nicknameTaken');
            const invalidMsg = t('auth.nicknameInvalid');
            if (prev.nickname === takenMsg || prev.nickname === invalidMsg) {
              return { ...prev, nickname: null };
            }
            return prev;
          });
        }
      } catch {
        // Ignore availability check failures; submit-time validation still applies.
      } finally {
        if (!cancelled) {
          setCheckingNickname(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [formData.nickname, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value } as any);
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleAccountContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.termsConsent) {
      setErrors({ termsConsent: t('auth.termsConsentRequired') });
      return;
    }
    if (errors.nickname) {
      return;
    }
    setErrors({});
    setStep('styles');
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStyles.length === 0) {
      setErrors({ preferredMusicStyles: t('auth.musicStylesRequired') });
      return;
    }
    if (errors.nickname) {
      return;
    }
    try {
      setSubmitting(true);
      setErrors({});
      const res = await fetch(getApiUrl('/api/v1/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          nickname: formData.nickname,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formData.phone.trim(),
          city: formData.city.trim(),
          postalCode: formData.postalCode.trim(),
          termsConsent: formData.termsConsent,
          preferredMusicStyles: selectedStyles,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        if (data?.errors) {
          const fieldErrors = mapApiErrorsToFields(data.errors);
          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            if (ACCOUNT_ERROR_FIELDS.some((field) => fieldErrors[field])) {
              setStep('account');
            }
            return;
          }
        }
        setErrors({
          password: translateApiError(t, {
            message: data?.message,
            code: data?.code,
            status: res.status,
          }),
        });
        setStep('account');
        return;
      }
      setSuccess(data?.msg || t('auth.registrationSuccess'));
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        password: translateApiError(t, { message: err?.message }),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleRegister = () => {
    window.location.href = buildGoogleOAuthUrl();
  };

  const legalLinks = {
    cgvLink: (
      <Link href={getLegalUrlWithReturn('cgv', i18n.language)} target="_blank" rel="noopener noreferrer" />
    ),
    privacyLink: (
      <Link href={getLegalUrlWithReturn('privacy', i18n.language)} target="_blank" rel="noopener noreferrer" />
    ),
  };

  return (
    <ConnectFormContainer>
      <Box sx={{ position: 'relative' }}>
        {submitting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              bgcolor: 'rgba(255, 255, 255, 0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '15px',
            }}
            aria-busy="true"
            aria-label={t('loading')}
          >
            <CircularProgress color="primary" size={48} />
          </Box>
        )}
      <MainLogo />
      {success ? (
        <>
          <Subtitle>{t('auth.registrationSuccess')}</Subtitle>
          <Box textAlign="center" my="1em">
            <Typography variant="subtitle1">{success}</Typography>
          </Box>
          <SmallLink to="/">{t('auth.returnHome')}</SmallLink>
        </>
      ) : step === 'styles' ? (
        <>
          <Subtitle>{t('auth.musicStylesTitle')}</Subtitle>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
            {t('auth.musicStylesDescription')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
            {t('auth.musicStylesHint')}
          </Typography>
          <form onSubmit={handleRegisterSubmit}>
            <MusicStyleSelector
              selected={selectedStyles}
              onChange={(styles) => {
                setSelectedStyles(styles);
                setErrors((prev) => ({ ...prev, preferredMusicStyles: null }));
              }}
              disabled={submitting}
            />
            {errors.preferredMusicStyles ? (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1, textAlign: 'center' }} role="alert">
                {errors.preferredMusicStyles}
              </Box>
            ) : null}
            <OutlineButton type="submit" disabled={submitting || selectedStyles.length === 0}>
              {t('auth.registerContinue')}
            </OutlineButton>
            <Button
              type="button"
              variant="text"
              onClick={() => setStep('account')}
              disabled={submitting}
              sx={{ display: 'block', mx: 'auto', textTransform: 'none' }}
            >
              {t('auth.registerBack')}
            </Button>
          </form>
        </>
      ) : (
        <>
          <Subtitle>{t('auth.register')}</Subtitle>
          <form onSubmit={handleAccountContinue} autoComplete="on">
            <TextInput
              label={t('auth.nickname')}
              name="nickname"
              autoComplete="nickname"
              fullWidth
              margin="normal"
              value={formData.nickname}
              onChange={handleChange}
              required
              error={Boolean(errors.nickname)}
              helperText={
                errors.nickname
                || (checkingNickname ? t('auth.checkingNickname') : undefined)
              }
              style={{ borderColor: errors.nickname ? 'red' : undefined }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextInput
                label={t('auth.firstName')}
                name="firstName"
                autoComplete="given-name"
                fullWidth
                margin="normal"
                value={formData.firstName}
                onChange={handleChange}
                required
                error={Boolean(errors.firstName)}
                helperText={errors.firstName || undefined}
                style={{ borderColor: errors.firstName ? 'red' : undefined }}
              />
              <TextInput
                label={t('auth.lastName')}
                name="lastName"
                autoComplete="family-name"
                fullWidth
                margin="normal"
                value={formData.lastName}
                onChange={handleChange}
                required
                error={Boolean(errors.lastName)}
                helperText={errors.lastName || undefined}
                style={{ borderColor: errors.lastName ? 'red' : undefined }}
              />
            </Box>
            <TextInput
              label={t('auth.phone')}
              name="phone"
              type="tel"
              autoComplete="tel"
              fullWidth
              margin="normal"
              value={formData.phone}
              onChange={handleChange}
              required
              error={Boolean(errors.phone)}
              helperText={errors.phone || undefined}
              style={{ borderColor: errors.phone ? 'red' : undefined }}
            />
            <TextInput
              label={t('auth.email')}
              name="email"
              type="email"
              autoComplete="email"
              fullWidth
              margin="normal"
              value={formData.email}
              onChange={handleChange}
              required
              error={Boolean(errors.email)}
              helperText={errors.email || undefined}
              style={{ borderColor: errors.email ? 'red' : undefined }}
            />
            <TextInput
              label={t('auth.password')}
              type="password"
              name="password"
              autoComplete="new-password"
              fullWidth
              margin="normal"
              value={formData.password}
              onChange={handleChange}
              required
              error={Boolean(errors.password)}
              helperText={errors.password || undefined}
              style={{ borderColor: errors.password ? 'red' : undefined }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {t('auth.locationPurpose')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 0.7fr' }, gap: 1.5 }}>
              <TextInput
                label={t('auth.city')}
                name="city"
                autoComplete="address-level2"
                fullWidth
                margin="normal"
                value={formData.city}
                onChange={handleChange}
                required
                error={Boolean(errors.city)}
                helperText={errors.city || undefined}
                style={{ borderColor: errors.city ? 'red' : undefined }}
              />
              <TextInput
                label={t('auth.postalCode')}
                name="postalCode"
                autoComplete="postal-code"
                fullWidth
                margin="normal"
                value={formData.postalCode}
                onChange={handleChange}
                required
                error={Boolean(errors.postalCode)}
                helperText={errors.postalCode || undefined}
                style={{ borderColor: errors.postalCode ? 'red' : undefined }}
              />
            </Box>
            <FormControlLabel
              control={
                <StyledCheckbox name="termsConsent" checked={formData.termsConsent} onChange={handleChange} />
              }
              label={
                <Typography variant="body2" component="span">
                  <Trans i18nKey="auth.termsConsentRich" components={legalLinks} />
                </Typography>
              }
              sx={{
                '& .MuiFormControlLabel-label': { color: (theme) => theme.palette.primary.main },
                mt: 1,
              }}
            />
            {errors.termsConsent ? (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 2 }} role="alert">
                {errors.termsConsent}
              </Box>
            ) : null}
            <OutlineButton type="submit" disabled={checkingNickname || Boolean(errors.nickname)}>
              {t('auth.registerContinue')}
            </OutlineButton>
            <WhiteDivider>{t('or')}</WhiteDivider>
            <GoogleButton onClick={handleGoogleRegister}>{t('auth.registerWithGoogle')}</GoogleButton>
            <Box textAlign="center">
              <SmallText>{t('auth.alreadyHaveAccount')}</SmallText>
              <SmallLink to="/">{t('auth.login')}</SmallLink>
            </Box>
          </form>
        </>
      )}
      </Box>
    </ConnectFormContainer>
  );
};

export default RegisterForm;
